/* eslint-disable guard-for-in */
/* eslint-disable no-restricted-syntax */
import querystring, { IParseOptions } from 'qs';
import { getEpLightOrigin, getFbOrigin } from './origin';

export const deduplicationParams = (
  record: Record<string, string | string[]>
): Record<string, string> => {
  const res: Record<string, string> = {};
  for (const key in record) {
    const value = record[key];
    if (Array.isArray(value)) {
      res[key] = value[value.length - 1];
    } else {
      res[key] = value as string;
    }
  }

  return res;
};

export const getURLParams = () => {
  return deduplicationParams(
    querystring.parse(window.location.search, {
      ignoreQueryPrefix: true,
      duplicates: 'last'
    } as IParseOptions) as Record<string, string>
  );
};

export const isIos = () => {
  return /(iPhone|iPad|iPod|iOS)/i.test(navigator.userAgent);
};

export const isAndroid = () => {
  return /(Android)/i.test(navigator.userAgent);
};

export const isWeChatMiniProgram = () => {
  return /(miniProgram)/i.test(navigator.userAgent);
};

export const isWeChat = () => {
  return isWeChatMiniProgram() || /(MicroMessenger)/i.test(navigator.userAgent);
};

export const isAliApp = () => {
  return /(AliApp)/i.test(navigator.userAgent);
};

export const isQuickApp = () => {
  return /hap/i.test(navigator.userAgent);
};

export const isBaiduApp = () => {
  return /(swan-baiduboxapp|swan-bdmap)/i.test(navigator.userAgent.toLowerCase());
};

export const isWeiboApp = () => {
  return /weibo/i.test(navigator.userAgent.toLowerCase());
};

export const isDouyinApp = () => {
  return /toutiaomicroapp/i.test(navigator.userAgent.toLowerCase());
};

export const isKuaiShouApp = () => {
  return (
    /kswebview/i.test(navigator.userAgent.toLowerCase()) ||
    /kuaishou/i.test(navigator.userAgent.toLowerCase())
  );
};

export const isMeituanApp = () => {
  return navigator.userAgent.includes('miniprogram MMP');
};

/** 飞书 */
export const isLark = () => {
  return /lark/i.test(navigator.userAgent.toLowerCase());
};

/** 货运 APP 内嵌企业 h5 */
export const isEpInApp = () => {
  const params = getURLParams();
  return params.ep_from === 'ep_h5';
};

export const isApp = () => {
  if (navigator.userAgent.toLowerCase().includes('huolala')) {
    return true;
  }
  if (isIos()) {
    return Boolean(window?.webkit?.messageHandlers?.app?.postMessage);
  }
  return Boolean(window?.app?.webcall);
};

export const isUApp = () => {
  return navigator.userAgent.toLowerCase().includes('huolalauapp');
};

export const isMobile = () => {
  return /iphone|ipod|android.*mobile|windows.*phone|blackberry.*mobile|harmonyos|openharmony/i.test(
    window.navigator.userAgent.toLowerCase()
  );
};

export const getIosVersion = () => {
  if (!isIos()) {
    return -1;
  }
  const match = navigator.userAgent.match(/OS ((\d+_?){2,3})\s/);
  if (!match) {
    return -1;
  }

  return Number(match[1].split('_').slice(0, 2).join('.'));
};

/**
 * 是否是小程序
 */
export const isMicroApp = () => {
  return (
    isWeChat() ||
    isAliApp() ||
    isQuickApp() ||
    isBaiduApp() ||
    isWeiboApp() ||
    isQuickApp() ||
    isDouyinApp() ||
    isMeituanApp() ||
    isKuaiShouApp() ||
    isLark()
  );
};

export const isXiaoLaChuXing = () => {
  return /.*\.xiaolachuxing\.com/i.test(window.location.hostname);
};

interface WebcallParams {
  action: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callback?: (cbParams: any) => void;
  [key: string]: unknown;
}

let listened = false;

const listenWebcall = () => {
  if (listened) {
    return;
  }

  listened = true;
  window.addEventListener(
    'message',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: MessageEvent<{ type: string; callback: string; callbackParams: any[] }>) => {
      const { type, callback, callbackParams = [] } = event.data;
      if (type === 'webcall-callback') {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (window[callback]) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          window[callback](...callbackParams);
        }
      }
    }
  );
};

export const iframeWebcall = (params: Omit<WebcallParams, 'callback'>) => {
  listenWebcall();
  window?.top?.postMessage({ type: 'webcall', params }, '*');
};

/**
 * 调用app事件
 * @see https://wiki.xlcx.work/pages/viewpage.action?pageId=14729183
 */
export const webcall = (params: WebcallParams) => {
  const callBackName = `__webcallCallback_${Date.now()}`;
  const callParams: Omit<WebcallParams, 'callback'> = {
    ...params
  };
  if (params.callback) {
    if (typeof params.callback !== 'function') {
      throw new Error('callback 为函数类型');
    }

    const { callback } = params;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window[callBackName] = cbParams => {
      callback(cbParams);

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window[callBackName] = null;
    };

    callParams.callback = callBackName;
  }
  // if (window.top !== window) {
  //   iframeWebcall(callParams);
  //   return;
  // }
  try {
    const message = JSON.stringify(callParams);
    console.log('webcall message', message);
    if (isIos() && window.webkit && window.webkit.messageHandlers) {
      window.webkit.messageHandlers.app.postMessage(message);
    } else if (window.app && window.app.webcall) {
      window.app.webcall(message);
    } else if (window.app && window.app.postMessage) {
      window.app.postMessage(message);
    } else {
      console.log('未检测到 webcall 方法');
    }
  } catch (error) {
    console.log('webcall error', error);
    return 'error';
  }
};

export const closeWebview = () => {
  if (isWeChatMiniProgram()) {
    window.wx?.miniProgram?.navigateBack();
  }
  if (isAliApp()) {
    window.my?.navigateBack();
  }
  if (isBaiduApp()) {
    window.swan?.webView?.navigateBack();
  }
  webcall({ action: 'closeWebView' });
  window.close();
};

/** TODO: 登录页面的 user_type 需要特殊转换 */
const transformUsertype = (identity?: string) => {
  if (identity === '2') {
    return '3';
  }
  if (identity === '3') {
    return '2';
  }
  return identity;
};

const goLoginPage = (identity?: string) => {
  const idenParam = `&user_type=${transformUsertype(String(identity))}`;
  const href = `${getFbOrigin()}/login/index.html?referrer=${encodeURIComponent(
    window.location.href
  )}${identity ? idenParam : ''}`;
  window.location.replace(href);
};

export const excludeURLParams = (url: string, params: string[]) => {
  try {
    const urlObj = new URL(url);
    params.forEach(key => {
      urlObj.searchParams.delete(key);
    });
    return urlObj.toString();
  } catch (err) {
    return url;
  }
};

export const goLogin = (identity?: string) => {
  try {
    if (isWeChatMiniProgram()) {
      return window?.wx?.miniProgram?.navigateTo({
        url: '/pages/login/index'
      });
    }
    if (isMeituanApp() && window?.wx?.miniProgram) {
      return setTimeout(() => {
        window?.wx?.miniProgram?.navigateTo({
          url: '/pages/login/index'
        });
      });
    }
    if (isAliApp()) {
      return window?.my?.navigateTo({ url: '/pages/login/index' });
    }
    if (isBaiduApp()) {
      return window?.swan?.webView?.navigateTo({ url: '/pages/login/index' });
    }

    if (isDouyinApp()) {
      return window?.tt?.redirectTo({ url: '/pages/login/index' });
    }

    if (isKuaiShouApp()) {
      return window?.ks?.redirectTo({ url: '/pages/login/index' });
    }

    if (isEpInApp()) {
      const { city_id, transport_token, stable } = getURLParams();
      window.location.replace(
        `${getEpLightOrigin(stable)}/?${querystring.stringify({
          city_id,
          transport_token,
          target: 'login_timeout',
          targetUrl: excludeURLParams(window.location.href, ['token', 'transform_token'])
        })}#/pages/login/index`
      );
      return;
    }

    if (isApp()) {
      webcall({ action: 'login' });
      webcall({ action: 'closeWebView' });
      return;
    }
    goLoginPage(identity);
  } catch (err) {
    goLoginPage(identity);
  }
};

export const callMiniAppAction = (action: string, params?: Record<string, string | undefined>) => {
  const callParams = params && Object.keys(params).length > 0 ? params : undefined;
  if (isWeChatMiniProgram()) {
    /**
     * @see https://developers.weixin.qq.com/miniprogram/dev/component/web-view.html
     */
    return window?.wx?.miniProgram?.[action as 'navigateTo'](callParams);
  }

  /**
   * @see https://h5.dianping.com/app/gfe-docs/mt-miniprogram-onlinedoc/component/open/web-view/#web-view
   */
  if (isMeituanApp()) {
    return setTimeout(() => {
      window?.wx?.miniProgram?.[action as 'navigateTo'](callParams);
    });
  }

  if (isAliApp()) {
    /**
     * @see https://opendocs.alipay.com/mini/component/web-view
     */
    return window?.my?.[action as 'navigateTo'](callParams);
  }

  if (isBaiduApp()) {
    /**
     * @see https://smartprogram.baidu.com/docs/develop/component/open_web-view/
     */
    return window?.swan?.webView?.[action as 'navigateTo'](callParams);
  }

  if (isDouyinApp()) {
    /**
     * @see https://developer.open-douyin.com/docs/resource/zh-CN/mini-app/develop/component/open-capacity/web-view/
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return window?.tt?.[action as 'navigateTo'](callParams as any);
  }
};

export const initMicroAppSdk = () => {
  let src = '';
  if (isWeChat()) {
    src = 'https://res.wx.qq.com/open/js/jweixin-1.3.2.js';
  }
  if (isAliApp()) {
    src = 'https://appx/web-view.min.js';
  }
  if (isBaiduApp()) {
    src = 'https://b.bdstatic.com/searchbox/icms/searchbox/js/swan-2.0.20.js';
  }

  if (isDouyinApp()) {
    src = 'https://lf1-cdn-tos.bytegoofy.com/goofy/developer/jssdk/jssdk-1.1.0.js';
  }

  if (isMeituanApp()) {
    src =
      'https://s3plus.meituan.net/v1/mss_2044229a1b824dbe990c4a549a07bfec/mmp/webview-jssdk-1.0.0.js';
  }

  if (src) {
    const script = document.createElement('script');
    script.setAttribute('type', 'text/javascript');
    script.setAttribute('src', src);
    document.body.appendChild(script);
  }
};

export const paddStart = (str: string, maxLength: number, fillString: string) => {
  if (str.length >= maxLength) {
    return str;
  }
  const length = maxLength - str.length;
  let start = '';
  for (let i = 0; i < length; i++) {
    start += fillString;
  }
  return start + str;
};

export const closePcWindow = () => {
  window?.top?.postMessage({ type: 'sdk-csc-im-message', event: 'close' }, '*');
};

export const hidePcWindow = () => {
  window?.top?.postMessage({ type: 'sdk-csc-im-message', event: 'hide' }, '*');
};

export const isInsideOrigin = (origin: string) => {
  const env = document.documentElement.dataset.vanEnv;
  if (env === 'stg' || env === 'pre') {
    return true;
  }
  return ['xiaolachuxing.com'].some(
    item => origin.endsWith(`.${item}`) || origin.endsWith(`//${item}`)
  );
};

export const isInsideURL = (url?: string | null) => {
  if (!url) {
    return false;
  }
  try {
    const { origin } = new URL(url);
    return isInsideOrigin(origin);
  } catch {
    return false;
  }
};

export const replaceSearch = (
  search?: string,
  extra: Record<string, string> = {},
  isInside?: boolean
) => {
  const { ...pageParams } = querystring.parse(window.location.search, {
    ignoreQueryPrefix: true
  });

  const insideParams = isInside ? pageParams : {};
  const params = search ? querystring.parse(search, { ignoreQueryPrefix: true }) : {};

  const newSearch = querystring.stringify({ ...insideParams, ...params, ...extra });
  return newSearch.startsWith('?') ? newSearch : `?${newSearch}`;
};

export const isHashMode = (hash: string) => {
  return /^#\/.*/.test(hash);
};

export const replaceHash = (
  hash?: string,
  extra: Record<string, string> = {},
  isInside?: boolean
) => {
  if (!hash) {
    return '';
  }
  if (isHashMode(hash)) {
    const [path, search] = hash.split('?');
    const newSearch = replaceSearch(search, extra, isInside);
    return path + (newSearch || '');
  }
  return hash;
};

export const replaceInsideURL = (url?: string) => {
  if (!url) {
    return url;
  }
  try {
    const { origin, pathname, search, hash } = new URL(url);
    if (!isInsideOrigin(origin)) {
      return url;
    }
    return `${origin}${pathname === '/' && !isHashMode(hash) ? '' : pathname}${
      !search && isHashMode(hash) ? search : replaceSearch(search, {}, true)
    }${replaceHash(hash, {}, true)}`;
  } catch (err) {
    return url;
  }
};

export const replaceURLWithParams = (url?: string, extra: Record<string, string> = {}) => {
  if (!url) {
    return url;
  }
  try {
    const { origin, pathname, search, hash } = new URL(url);

    const isInside = isInsideOrigin(origin);
    const newPathname = pathname === '/' && !isHashMode(hash) ? '' : pathname;
    const newSearch = !search && isHashMode(hash) ? search : replaceSearch(search, extra, isInside);
    const newHash = replaceHash(hash, extra, isInside);

    return `${origin}${newPathname}${newSearch}${newHash}`;
  } catch (err) {
    return url;
  }
};

export const isProd = () => {
  return document.documentElement.dataset.vanEnv === 'prod';
};

export const WECHAT_APP_CODE_MAP = {
  /** 货运小程序 */
  FREIGHT: 'gh_6b18e73b21cd',
  /** 搬家小程序 */
  HOUSE_MOVING: 'gh_14fd961f626e',
  /** 物流小程序 */
  LOGISTICS: 'gh_886c60b8b769',
  /** 企业小程序 */
  ENTERPRISE: 'gh_98b1ac705cfb',
  /** 营销小程序 */
  MARKAETING: 'gh_f2061d4fe0c1',
  DRIVER: 'gh_0fc95f50f6dd',
  XIAOLA: 'gh_53d3b8626252'
};

const WECHAT_URI_SCHEME = [
  { prefix: 'wechat', code: WECHAT_APP_CODE_MAP.FREIGHT },
  { prefix: 'wechat-banjia', code: WECHAT_APP_CODE_MAP.HOUSE_MOVING },
  { prefix: 'wechat-ep', code: WECHAT_APP_CODE_MAP.ENTERPRISE },
  { prefix: 'wechat-logistics', code: WECHAT_APP_CODE_MAP.LOGISTICS },
  { prefix: 'wechat-marketing', code: WECHAT_APP_CODE_MAP.MARKAETING },
  { prefix: 'wechat-xiaola', code: WECHAT_APP_CODE_MAP.XIAOLA }
];

export const isWechatURI = (uri?: string) => {
  if (!uri) {
    return false;
  }

  return WECHAT_URI_SCHEME.some(item => uri.startsWith(`${item.prefix}://`));
};

export const isAppURI = (uri?: string) => {
  if (!uri) {
    return false;
  }

  return uri.startsWith(`hll://`);
};

export const isAppActionURI = (uri?: string) => {
  if (!uri) {
    return false;
  }

  return uri.startsWith(`hll-app://`);
};

export const isMiniAppActionURI = (uri?: string) => {
  if (!uri) {
    return false;
  }

  return uri.startsWith(`mini-app://`);
};

export const getMiniAppActionURIInfo = (uri?: string) => {
  if (!uri) {
    return {};
  }

  try {
    const parts = uri.match(/^(mini-app):\/\/([\w|.|-]+)(\?[\w|-|=|&|/]+)?/);
    if (!parts) {
      return {};
    }

    const params = querystring.parse(parts[3], { ignoreQueryPrefix: true });

    return { pathname: parts[2], params };
  } catch (err) {
    return {};
  }
};

export const getAppActionURIInfo = (uri?: string) => {
  if (!uri) {
    return {};
  }

  try {
    const parts = uri.match(/^(hll-app):\/\/([\w|.|-]+)(\?[\w|-|=|&|/|%|.|:]+)?/);
    if (!parts) {
      return {};
    }

    const params = querystring.parse(parts[3], { ignoreQueryPrefix: true });

    return { pathname: parts[2], params };
  } catch (err) {
    return {};
  }
};

export const parseAppActionLinkParams = (params: Record<string, string>) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const newParams: Record<string, any> = {};
  // eslint-disable-next-line no-restricted-syntax, guard-for-in
  for (const key in params) {
    if (!key.includes('.')) {
      newParams[key] = params[key];
      // eslint-disable-next-line no-continue
      continue;
    }

    const keyList = key.split('.');
    let current = newParams;
    for (let i = 0; i < keyList.length; i++) {
      const subKey = keyList[i];
      if (i === keyList.length - 1) {
        current[subKey] = params[key];
        break;
      }

      if (!current[subKey] || typeof current[subKey] !== 'object') {
        current[subKey] = {};
        current = current[subKey];
      } else {
        current = current[subKey];
      }
    }
  }

  const res: Record<string, string> = {};
  // eslint-disable-next-line no-restricted-syntax, guard-for-in
  for (const key in newParams) {
    if (typeof newParams[key] === 'object') {
      res[key] = JSON.stringify(newParams[key]);
    } else {
      res[key] = newParams[key];
    }
  }

  return res;
};

export const redirectWithParams = (address?: string) => {
  const href = !address || !isInsideURL(address) ? address : replaceInsideURL(address);
  window.location.href = href || '';
};

export const openWechatApp = (uri?: string) => {
  if (!uri) {
    return;
  }

  const target = WECHAT_URI_SCHEME.find(item => uri.startsWith(`${item.prefix}://`));

  if (target) {
    const targetUrl = uri.replace(`${target.prefix}://`, 'https://');
    if (isApp()) {
      webcall({
        action: 'openWXMiniprogram',
        appName: target.code,
        miniprogramType: isProd() ? 0 : 2,
        path: `pages/home/index?target=adv&targetUrl=${encodeURIComponent(targetUrl)}`
      });

      return;
    }
    redirectWithParams(targetUrl);
    return;
  }

  if (uri.startsWith('https://')) {
    redirectWithParams(uri);
  }
};

export const openAppPage = (uri?: string) => {
  webcall({ action: 'openApp', uri });
};

type JSONBaseType = {
  string: string | null;
  number: number | null;
  boolean: boolean | null;
  object: Record<string, unknown>;
  array: unknown[];
};

const getJSONDefault = (type: keyof JSONBaseType) => {
  if (type === 'object') {
    return {};
  }
  if (type === 'array') {
    return [];
  }
  return null;
};

export const parseJSON = <T extends keyof JSONBaseType, V = JSONBaseType[T]>(
  type: T,
  json?: string
): V => {
  if (!json) {
    return getJSONDefault(type) as V;
  }
  try {
    const res = JSON.parse(json);
    if (!res) {
      return getJSONDefault(type) as V;
    }
    return res;
  } catch (err) {
    return getJSONDefault(type) as V;
  }
};

export const trimObjectKeys = <T extends Record<string, unknown> = Record<string, unknown>>(
  obj: T
): T => {
  const newObj: T = {} as T;
  // eslint-disable-next-line no-restricted-syntax, guard-for-in
  for (const key in obj) {
    (newObj as Record<string, unknown>)[key.trim()] = obj[key];
  }

  return newObj;
};

export default webcall;
